<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import linkedinClient from 'dashboard/api/channel/linkedinClient';
import Button from 'dashboard/components-next/button/Button.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const { t } = useI18n();

const hasError = ref(false);
const errorStateMessage = ref('');
const errorStateDescription = ref('');
const isRequestingAuthorization = ref(false);

// The tile that leads here stays clickable whether or not the installation has
// LinkedIn credentials, so this screen is where an admin finds out they are
// missing. Without a client id the authorize URL would send them to LinkedIn
// with an empty `client_id` and a generic LinkedIn error page, which says
// nothing about what to fix — so the button is disabled and the setup steps are
// shown instead.
const isConfigured = computed(() => !!window.chatwootConfig?.linkedinAppId);

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const errorCode = urlParams.get('code');
  const errorMessage = urlParams.get('error_message');

  if (errorMessage) {
    hasError.value = true;
    if (errorCode === '400') {
      errorStateMessage.value = errorMessage;
      errorStateDescription.value = t('INBOX_MGMT.ADD.LINKEDIN.ERROR_AUTH');
    } else {
      errorStateMessage.value = t('INBOX_MGMT.ADD.LINKEDIN.ERROR_MESSAGE');
      errorStateDescription.value = errorMessage;
    }
  }
  // User need to remove the error params from the url to avoid the error to be shown again after page reload, so that user can try again
  const cleanURL = window.location.pathname;
  window.history.replaceState({}, document.title, cleanURL);
});

const requestAuthorization = async () => {
  isRequestingAuthorization.value = true;
  const response = await linkedinClient.generateAuthorization();
  const {
    data: { url },
  } = response;

  window.location.href = url;
};
</script>

<template>
  <div class="h-full p-6 w-full max-w-full flex-shrink-0 flex-grow-0">
    <div class="flex flex-col items-center justify-start h-full text-center">
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
        />
      </div>
      <div
        v-else
        class="flex flex-col items-center justify-center w-full px-8 py-10 text-center rounded-2xl outline outline-1 outline-n-weak"
      >
        <div class="flex flex-col items-center w-full max-w-2xl">
          <h6 class="text-2xl font-medium">
            {{ $t('INBOX_MGMT.ADD.LINKEDIN.CONNECT_YOUR_LINKEDIN_PROFILE') }}
          </h6>
          <p class="max-w-xl py-6 text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.LINKEDIN.HELP') }}
          </p>

          <Button
            class="text-white !rounded-full !px-6 bg-[#0A66C2] hover:bg-[#004182]"
            lg
            icon="i-ri-linkedin-fill"
            :disabled="isRequestingAuthorization || !isConfigured"
            :is-loading="isRequestingAuthorization"
            :label="$t('INBOX_MGMT.ADD.LINKEDIN.CONTINUE_WITH_LINKEDIN')"
            @click="requestAuthorization()"
          />
          <Banner v-if="!isConfigured" color="blue" class="w-full mt-6">
            <div class="flex items-start gap-3 text-left">
              <Icon icon="i-lucide-info" class="flex-shrink-0 size-4 mt-0.5" />
              <span>
                {{ $t('INBOX_MGMT.ADD.LINKEDIN.SETUP_REQUIRED') }}
              </span>
            </div>
          </Banner>
          <!--
            Page comments work (polled every 30 minutes, replies posted as the
            page). Page DIRECT MESSAGES do not: LinkedIn restricts them to
            approved Community Management API partners.
          -->
          <Banner v-if="isConfigured" color="blue" class="w-full mt-6">
            <div class="flex items-start gap-3 text-left">
              <Icon icon="i-lucide-info" class="flex-shrink-0 size-4 mt-0.5" />
              <span>
                {{ $t('INBOX_MGMT.ADD.LINKEDIN.APPROVAL_PENDING_WARNING') }}
              </span>
            </div>
          </Banner>
        </div>
      </div>
    </div>
  </div>
</template>
